import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  CompanyMachineSchema,
  IndustrialProcessSchema,
  MachineSchema,
} from "../generated/prisma/zod";
import { MaterialUploadResponseSchema } from "../types/MaterialUploadError";

const c = initContract();

export const companyMachinesContract = c.router({
  create: {
    method: "POST",
    path: "/company-machines",
    responses: {
      201: CompanyMachineSchema,
      400: z.object({ message: z.string() }),
    },
    body: z.object({
      machineId: z.string().optional(),
      machineHourlyPrice: z.number(),
      materialLoadTimeMinutes: z.number().nullable().optional(),
      marginRatio: z.number().optional(),
      modelProcessStepDescription: z.string().nullable().optional(),
      processStepDescriptionOverride: z.string().nullable().optional(),
      cycleTimeFormula: z.string().nullable().optional(),
      preparationTimeFormula: z.string().nullable().optional(),
      canBeOptimized: z.boolean().optional(),
      machine: z
        .object({
          name: z.string(),
          brand: z.string().optional(),
          industrialProcess: IndustrialProcessSchema,
        })
        .optional(),
    }),
    summary: "Create a company machine",
  },

  update: {
    method: "PATCH",
    path: "/company-machines/:id",
    body: CompanyMachineSchema.omit({
      companyId: true,
      createdAt: true,
      updatedAt: true,
      machineId: true,
      id: true,
    })
      .extend({
        machineId: z.string().optional(),
        machine: z
          .object({
            name: z.string(),
            brand: z.string().optional(),
            industrialProcess: IndustrialProcessSchema,
          })
          .optional(),
      })
      .deepPartial(),
    responses: {
      200: CompanyMachineSchema.merge(
        z.object({
          machine: MachineSchema.optional(),
        }),
      ).nullable(),
      400: z.object({ message: z.string() }),
    },
    summary: "Update a company machine",
  },

  findAll: {
    method: "GET",
    path: "/company-machines",
    responses: {
      200: CompanyMachineSchema.merge(
        z.object({
          machine: MachineSchema,
        }),
      ).array(),
    },
  },

  delete: {
    method: "DELETE",
    path: "/company-machines/:id",
    body: z.object({}).optional(),
    responses: {
      200: CompanyMachineSchema,
    },
    summary: "Delete a company machine",
  },

  deleteAll: {
    method: "DELETE",
    path: "/company-machines",
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    summary: "Delete all company machines for the requesting company",
  },

  uploadCustomMachines: {
    method: "POST",
    path: "/company-machines/upload",
    contentType: "multipart/form-data",
    responses: {
      200: MaterialUploadResponseSchema,
      400: MaterialUploadResponseSchema,
    },
    body: c.type<{ file: File }>(),
    summary: "Upload a machine CSV file for a company",
  },
});
